/**
 * Pure decision rules for connecting to a LIVE, kept out of session.ts so they can be unit
 * tested (test/liveflow.test.ts). No timers, no network, no DOM.
 */

import { EULER_CLOSE, type PageFeatures } from '../../shared/events';

export const NOT_LIVE_RETRY_MS = 30_000;
export const NOT_LIVE_MAX_TRIES = 10;
/** fastLiveCheck: how often a waiting whitelisted page asks TikTok whether the LIVE started. */
export const FAST_CHECK_MS = 10_000;
/** fastLiveCheck: how long the Live and Reader pages keep waiting (overlays wait forever). */
export const FAST_CHECK_WINDOW_MS = 60 * 60 * 1000;
/** Relay errors in one re-check cycle before giving up on the relay for that cycle. */
export const RELAY_MAX_FAILURES = 3;

export const NO_FEATURES: PageFeatures = { silenceWatchdog: false, fastLiveCheck: false, diagnostics: false, overlayRejoin: false };

/**
 * Whether a relay close counts as a relay *failure*. "Not live" (4404), "stream ended" (4005),
 * "login expired" (4401, handled separately) and a normal close are answers, not failures; only
 * real errors (1006, 1011, 4500, anything unexpected) count.
 */
export function relayCloseIsFailure(code: number): boolean {
  return ![EULER_CLOSE.NORMAL, EULER_CLOSE.NOT_LIVE, EULER_CLOSE.STREAM_END, EULER_CLOSE.INVALID_AUTH].includes(code as never);
}

/**
 * silenceWatchdog: how long a "live" page may go without any event before re-joining, given how
 * many silent re-joins it already made. 90 s, 3 min, then every 5 min: about 6 re-joins over a
 * 20-minute wait before the broadcast (each relay re-join is one more login of the test account).
 */
export function silenceThresholdMs(rejoins: number): number {
  return [90_000, 180_000, 300_000][Math.min(Math.max(rejoins, 0), 2)];
}

export interface WaitInput {
  features: PageFeatures;
  whitelisted: boolean;
  /** Overlays: never stop waiting. */
  retryForever: boolean;
  /** Re-checks already made in this wait. */
  tries: number;
  /** Milliseconds since this wait started. */
  waitedMs: number;
  /** 'offline' = TikTok says not live; 'restricted' = live, but nothing could open it. */
  status: 'offline' | 'restricted';
}

export type WaitPlan =
  /** Ask TikTok's cheap public check after delayMs; only reconnect once it says live. */
  | { kind: 'poll'; delayMs: number }
  /** Reconnect the full path (slot, Euler, login, relay) after delayMs. */
  | { kind: 'reopen'; delayMs: number }
  /** Stop and wait for the person to tap "Try again". */
  | { kind: 'stop' };

/** What a page does while the account is not live (or the LIVE could not be opened yet). */
export function planWait(i: WaitInput): WaitPlan {
  const fast = i.features.fastLiveCheck && i.whitelisted;
  const withinLimit = i.retryForever || (fast ? i.waitedMs < FAST_CHECK_WINDOW_MS : i.tries < NOT_LIVE_MAX_TRIES);
  if (!withinLimit) return { kind: 'stop' };
  // Polling only helps while TikTok says "not live". A restricted LIVE is already live on TikTok,
  // so polling would reconnect every 10 s; keep the gentler 30 s full re-check for that.
  if (fast && i.status === 'offline') return { kind: 'poll', delayMs: FAST_CHECK_MS };
  return { kind: 'reopen', delayMs: NOT_LIVE_RETRY_MS };
}
